import styled from "@emotion/styled";
import { Box, Stack, Typography } from "@mui/material";
import { ReactComponent as BusinessIcon } from "Components/_Shared/Icons/Assets/business.svg";
import { ReactComponent as ChildIcon } from "Components/_Shared/Icons/Assets/child.svg";
import { ReactComponent as FinancialSuccessIcon } from "Components/_Shared/Icons/Assets/financial-success.svg";
import { ReactComponent as GrandfatherIcon } from "Components/_Shared/Icons/Assets/grandfather.svg";
import { ReactComponent as HomeIcon } from "Components/_Shared/Icons/Assets/home.svg";
import { ReactComponent as RefundIcon } from "Components/_Shared/Icons/Assets/refund.svg";
import { ReactComponent as TargetIcon } from "Components/_Shared/Icons/Assets/target.svg";
import { IncomeIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";
import type { FC } from "react";

type Props = {
  active?: boolean;
  incomeType: IncomeIncomeType;
  onIncomeSelect: (income: IncomeIncomeType) => void;
  disabled?: boolean;
};

const StyledWrapper = styled(Box, {
  shouldForwardProp: isPropValid,
})<{ $active: boolean; $disabled: boolean }>`
  &:hover {
    cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  }

  ${({ theme, $active }) => `
    box-sizing: border-box;
    height: ${theme.spacing(16)};
    border: ${$active ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.colors.borderLight}`};
    border-radius: ${theme.spacing(Radius.M)};
    padding: ${$active ? theme.spacing(2.8) : theme.spacing(Spacing.L)};
    box-shadow: ${theme.shadows[1]};
    background: ${$active ? theme.palette.primary.light : theme.colors.white};

    svg {
      color: ${$active ? theme.palette.text.primary : theme.palette.text.secondary}
    }
  `}
`;

const getIcon = (incomeType: IncomeIncomeType) => {
  switch (incomeType) {
    case IncomeIncomeType.employment:
      return <BusinessIcon />;
    case IncomeIncomeType.self_employment:
      return <FinancialSuccessIcon />;
    case IncomeIncomeType.rental:
      return <HomeIcon />;
    case IncomeIncomeType.parental:
      return <ChildIcon />;
    case IncomeIncomeType.pension:
      return <GrandfatherIcon />;
    case IncomeIncomeType.disability:
      return <GrandfatherIcon />;
    case IncomeIncomeType.orphan_or_widow_pension:
      return <RefundIcon />;
    case IncomeIncomeType.other:
      return <TargetIcon />;
  }
};

export const getIncomeTypeResource = (incomeType: IncomeIncomeType) => {
  switch (incomeType) {
    case IncomeIncomeType.employment:
      return Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.Employment;
    case IncomeIncomeType.self_employment:
      return Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.SelfEmployment;
    case IncomeIncomeType.rental:
      return Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.Rental;
    case IncomeIncomeType.parental:
      return Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.Parental;
    case IncomeIncomeType.pension:
      return Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.Pension;
    case IncomeIncomeType.disability:
      return Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.Disability;
    case IncomeIncomeType.orphan_or_widow_pension:
      return Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.OrphanOrWidowPension;
    case IncomeIncomeType.other:
      return Resources.AnalysisDetail.Budget.Incomes.IncomeTypes.Other;
  }
};

export const IncomeTypeTile: FC<Props> = ({ active = false, incomeType, onIncomeSelect, disabled = false }) => {
  const { t } = useResource();

  return (
    <StyledWrapper $active={active} $disabled={disabled} onClick={() => !disabled && onIncomeSelect(incomeType)}>
      <Stack spacing={Spacing.M}>
        {getIcon(incomeType)}

        <Typography variant="caption" color={active ? undefined : "secondary"}>
          {t(getIncomeTypeResource(incomeType))}
        </Typography>
      </Stack>
    </StyledWrapper>
  );
};
